import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Container,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useAuth } from '../services/AuthService';
import { useColorMode } from '../hooks/useColorMode';
import { useUserType } from '../hooks/useUserType';
import UserTypeService from '../services/UserTypeService';
import AdminService from '../services/AdminService';

const NAV_ITEMS = [
  { label: 'Jobs', href: '/jobs' },
  { label: 'Dashboard', href: '/dashboard' },
];

const NavBar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated, logout, loginWithRedirect, signupWithRedirect, isAdmin } = useAuth();
  const { userTypeData } = useUserType();
  const { mode, toggleColorMode } = useColorMode();
  const userTypeService = new UserTypeService();
  const navigate = useNavigate();

  const handleAdminPanelNavigate = async () => {
    try {
      const { url } = await AdminService.getAdminNavigationUrl('admin-panel');
      navigate(url);
    } catch (err) {
      console.error('Failed to get admin navigation URL:', err);
    }
  };

  const handleUserTypeToggle = () => {
    const newType = userTypeData.userType === UserTypeService.USER_TYPES.COMPANY
      ? UserTypeService.USER_TYPES.JOB_SEEKER
      : UserTypeService.USER_TYPES.COMPANY;

    userTypeService.setUserTypePreference(newType);
    window.location.reload();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const activeProfile = userTypeData?.userType === UserTypeService.USER_TYPES.COMPANY
    ? userTypeData?.profiles?.company
    : userTypeData?.profiles?.jobSeeker;
  const firstName = activeProfile?.basicProfile.firstName || '';

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          // Override theme default to ensure glass effect is visible
          bgcolor: 'transparent',
          backgroundImage: 'none',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: 64 }}>
            {/* Logo */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 4 }}>
              <Box
                component="img"
                src="/Labourando.png"
                alt="Labourando"
                sx={{ height: 40, objectFit: 'contain' }}
              />
              <Typography
                component={RouterLink}
                to="/"
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  textDecoration: 'none',
                  letterSpacing: '0.05em',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                LABOURANDO
              </Typography>
            </Stack>

            {/* Desktop Nav Links */}
            <Stack
              direction="row"
              spacing={2}
              sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}
            >
              {NAV_ITEMS.map((item) => (
                <Button
                  key={item.label}
                  component={RouterLink}
                  to={item.href}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    transition: 'color 0.2s',
                    '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              {isAdmin && (
                <Button
                  onClick={handleAdminPanelNavigate}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    transition: 'color 0.2s',
                    '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
                  }}
                >
                  Admin Panel
                </Button>
              )}
            </Stack>

            {/* Desktop Auth Buttons */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              <IconButton
                onClick={toggleColorMode}
                color="inherit"
                size="small"
                aria-label="Toggle dark mode"
              >
                {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
              {!isAuthenticated ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={loginWithRedirect}
                    sx={{ textTransform: 'none' }}
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={signupWithRedirect}
                    sx={{
                      textTransform: 'none',
                      px: 2,
                    }}
                  >
                    Sign Up
                  </Button>
                </>
              ) : (
                <>
                  {userTypeData?.hasCompanyProfile && userTypeData?.hasJobSeekerProfile && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userTypeData.userType === UserTypeService.USER_TYPES.COMPANY}
                          onChange={handleUserTypeToggle}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                          {userTypeData.userType === UserTypeService.USER_TYPES.COMPANY ? 'Company' : 'Job Seeker'}
                        </Typography>
                      }
                      sx={{ mr: 2 }}
                    />
                  )}
                  {firstName && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
                      Hi, {firstName}
                    </Typography>
                  )}
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                    sx={{ textTransform: 'none' }}
                  >
                    Sign Out
                  </Button>
                </>
              )}
            </Stack>

            {/* Mobile Menu Button */}
            <IconButton
              color="inherit"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ display: { md: 'none' }, ml: 'auto' }}
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="top"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ display: { md: 'none' } }}
      >
        <Box sx={{ p: 2 }}>
          <List>
            {NAV_ITEMS.map((item) => (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.href}
                  onClick={handleDrawerToggle}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
                {isAdmin && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => { handleAdminPanelNavigate(); handleDrawerToggle(); }}
                    >
                      <ListItemText primary="Admin Panel" />
                    </ListItemButton>
                  </ListItem>
                )}
          </List>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {mode === 'dark' ? 'Dark Mode' : 'Light Mode'}
            </Typography>
            <IconButton
              onClick={toggleColorMode}
              size="small"
              aria-label="Toggle dark mode"
            >
              {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Stack spacing={2}>
            {!isAuthenticated ? (
              <>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => { loginWithRedirect(); handleDrawerToggle(); }}
                  sx={{ color: 'primary.main', borderColor: 'primary.main' }}
                >
                  Sign In
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => { signupWithRedirect(); handleDrawerToggle(); }}
                  sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                  Sign Up
                </Button>
              </>
            ) : (
              <>
                {firstName && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    Hi, {firstName}
                  </Typography>
                )}
                {userTypeData?.hasCompanyProfile && userTypeData?.hasJobSeekerProfile && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={userTypeData.userType === UserTypeService.USER_TYPES.COMPANY}
                        onChange={() => { handleUserTypeToggle(); handleDrawerToggle(); }}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {userTypeData.userType === UserTypeService.USER_TYPES.COMPANY ? 'Company' : 'Job Seeker'}
                      </Typography>
                    }
                    sx={{ mb: 1 }}
                  />
                )}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => { logout({ logoutParams: { returnTo: window.location.origin } }); handleDrawerToggle(); }}
                >
                  Sign Out
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

export default NavBar;
